/**
 * Helper function to extract error message from different error structures
 * @param error Any type of error
 * @returns Extracted error message as string
 */

export const getErrorMessage = (error: unknown): string => {
  const defaultMessage = 'Something went wrong';

  try {
    // If error is null or undefined
    if (!error) {
      return defaultMessage;
    }

    // If error is a string, return it directly
    if (typeof error === 'string') {
      return error;
    }

    // If error is an Error instance
    if (error instanceof Error) {
      return error.message;
    }

    // If error is an object
    if (typeof error === 'object') {
      const errorObj = error as Record<string, unknown>;

      // Check common error message patterns
      if ('message' in errorObj && typeof errorObj.message === 'string') {
        return errorObj.message;
      }

      if ('error' in errorObj && typeof errorObj.error === 'string') {
        return errorObj.error;
      }

      if ('data' in errorObj && typeof errorObj.data === 'object' && errorObj.data) {
        const data = errorObj.data as Record<string, unknown>;

        // Handle structured validation errors - extract first meaningful error
        if ('errors' in data && typeof data.errors === 'object' && data.errors) {
          const errors = data.errors as Record<string, unknown>;
          const firstError = Object.values(errors)[0];
          if (Array.isArray(firstError) && firstError.length > 0) {
            return firstError[0];
          }
        }

        if ('message' in data && typeof data.message === 'string') {
          return data.message;
        }

        if ('error' in data && typeof data.error === 'string') {
          return data.error;
        }
      }

      // If error has a response object (axios error structure)
      if ('response' in errorObj && typeof errorObj.response === 'object' && errorObj.response) {
        const response = errorObj.response as Record<string, unknown>;

        if ('data' in response && typeof response.data === 'object' && response.data) {
          const data = response.data as Record<string, unknown>;

          // Handle structured validation errors in response - extract first meaningful error
          if ('errors' in data && typeof data.errors === 'object' && data.errors) {
            const errors = data.errors as Record<string, unknown>;
            const firstError = Object.values(errors)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              return firstError[0];
            }
          }

          if ('message' in data && typeof data.message === 'string') {
            return data.message;
          }

          if ('error' in data && typeof data.error === 'string') {
            return data.error;
          }
        }

        // Check response.message
        if ('message' in response && typeof response.message === 'string') {
          return response.message;
        }
      }

      // If we can stringify the error object
      try {
        const stringified = JSON.stringify(error);
        if (stringified && stringified !== '{}') {
          return stringified;
        }
      } catch {
        return defaultMessage;
      }
    }

    return defaultMessage;
  } catch {
    return defaultMessage;
  }
};
